import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal, ViewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { firstValueFrom } from 'rxjs';
import { I18nService } from '../../../../core/services/i18n.service';
import { RuntimeConfigService } from '../../../../core/services/runtime-config.service';
import { SpsPaymentStatusTagComponent } from '../../../../custom/components/sps-payment-status-tag/sps-payment-status-tag.component';
import { MotoInitResponse } from '../../../interfaces/ifs-moto/moto-init-response';
import { MotoPaymentInitRequest } from '../../../interfaces/ifs-moto/moto-payment-init-request';
import { MotoPaymentRequest } from '../../../interfaces/ifs-moto/moto-payment-request';
import { MotoPaymentResult } from '../../../interfaces/ifs-moto/moto-payment-result';
import { IfsCreditCardComponent } from '../ifs-credit-card/ifs-credit-card.component';
import { PagateqIfsInstance, PaymentTypeStatus } from '../../../interfaces/ifs-moto/pagateq-ifs.types';

/**
 * Payment steps for the dialog
 */
type Step = 'form' | 'card' | 'done';

@Component({
  selector: 'sps-moto-payment-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    DialogModule,
    ButtonModule,
    MessageModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DividerModule,
    TagModule,
    IfsCreditCardComponent,
    SpsPaymentStatusTagComponent
  ],
  templateUrl: './moto-payment-dialog.component.html',
})
export class MotoPaymentDialogComponent {

  /**
   * Reference on the form inputs to reset the invalid flags
   */
  @ViewChild('orderIdField') orderIdField!: NgModel;
  @ViewChild('amountField') amountField!: NgModel;

  /**
   * Service injections
   * @private
  */
  private readonly http = inject(HttpClient);
  private readonly runtimeConfig = inject(RuntimeConfigService);
  readonly i18n = inject(I18nService);

  // --- Sichtbarkeit ---
  readonly visible = model<boolean>(false);

  /**
   * Backend-Identifier des MOTO-Payment-Types. Das Backend entscheidet anhand
   * dieser ID, ob es Test- oder Live-Credentials verwendet.
   */
  readonly projectId = input.required<number>();

  /**
   * Tenant-ID für Backend
   */
  readonly tenantId = input.required<string>();

  /**
   * Payment Type title
   */
  readonly paymentTypeTitle = input.required<string>();

  /**
   * Steuert nur das Frontend (SDK-URL-Auswahl + UI-Hinweis). Default = 'TEST'.
   * Der Aufrufer setzt dies konsistent zur projectId.
   */
  readonly paymentTypeStatus = input<PaymentTypeStatus>('TEST');
  readonly showTestHint = input<boolean>(true);
  // --- Optionale Vorbelegung - aktuell für GC nur EUR ---
  readonly currencyOptions = input<string[]>(['EUR']);

  // --- Dialog-Verhalten ---
  readonly closable = input<boolean | null>(null);
  readonly closeOnEscape = input<boolean | null>(null);
  readonly showCancelButton = input<boolean>(true);
  readonly showBackButton = input<boolean>(true);
  readonly appendTo = input<'body' | HTMLElement | null>('body');
  readonly width = input<string>('520px');
  readonly styleClass = input<string>('');
  readonly breakpoints = input<Record<string, string>>({ '960px': '90vw' });

  // --- Labels ---
  readonly payLabel = input<string>(this.i18n.i18nLng()['PAY']);
  readonly doneLabel = input<string>(this.i18n.i18nLng()['CLOSE']);
  readonly successText = input<string>(this.i18n.i18nLng()['PAYMENTSUCCESSFUL']);

  // --- Outputs ---
  readonly completed = output<any>();
  readonly cancelled = output<void>();
  readonly closed = output<void>();

  /**
   * Input-signals for the pyment initialization
   */
  readonly orderIdInput = signal<string>('');
  readonly amountInput = signal<number | null>(null);
  readonly currencyInput = signal<string>('EUR');

  /**
   * payment status and configuration signals
   */
  readonly step = signal<Step>('form');
  readonly sessionId = signal<string | null>(null);
  readonly reference = signal<string | null>(null);
  readonly clientConfiguration = signal<string | null>(null);
  readonly ifsInstance = signal<PagateqIfsInstance | null>(null);
  readonly initializing = signal(false);
  readonly ifsFormValid = signal(false);
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * dialog style -> computes on style inputs
   */
  readonly dialogStyle = computed(() => ({ width: this.width() }));

  /**
   * Validation of the initialization data
   */
  readonly formValid = computed(() => {
    const id = this.orderIdInput().trim();
    const amount = this.amountInput();
    const cur = this.currencyInput();
    return id.length > 0 && amount !== null && amount > 0 && !!cur;
  });

  /**
   * payment in progress - disable anything that could brake the payment
   */
  readonly busy = computed(() => this.initializing() || this.submitting());

  /**
   * disable the initialization data form
   */
  readonly formDisabled = computed(() =>
    this.initializing() || this.submitting() || this.step() === 'card' || this.step() === 'done',
  );

  /**
   * check if the dialog can be closed
   */
  readonly effectiveClosable = computed(() => {
    if (this.busy()) return false;
    return this.closable() ?? true;
  });

  /**
   * check if close on escape could be fired
   */
  readonly effectiveCloseOnEscape = computed(() => {
    if (!this.effectiveClosable()) return false;
    return this.closeOnEscape() ?? true;
  });

  constructor() {
    /**
     * Initialize the initialization request
     */
    this.applyInitialRequest();
  }

  /**
   * Step 1 = payment initialization over girocheckout api
   */
  async initSession(): Promise<void> {
    if (!this.formValid() || this.initializing()) return;
    const req = this.currentRequest();
    const projectId = this.projectId();
    const mode = this.paymentTypeStatus();
    this.error.set(null);
    this.initializing.set(true);
    try {
      const init = await firstValueFrom(
        this.http.post<MotoInitResponse>(this.runtimeConfig.transactionHandlingBackend + this.runtimeConfig.ifsInitEndpoint, { ...req }),
      );
      if (!init?.clientSession) throw new Error(this.i18n.i18nLng()['IFSERRORNOSESSION']);
      this.sessionId.set(init.clientSession);
      this.reference.set(init.reference);
      this.clientConfiguration.set(init.clientConfiguration);
      this.step.set('card');
    } catch (err) {
      this.error.set(this.toMessage(err) ?? this.i18n.i18nLng()['IFSINITFAILED']);
    } finally {
      this.initializing.set(false);
    }
  }

  /**
   * Back to the initialization form (Back-Button)
   */
  backToForm(): void {
    if (this.submitting()) return;
    this.ifsInstance.set(null);
    this.sessionId.set(null);
    this.clientConfiguration.set(null);
    this.error.set(null);
    this.step.set('form');
  }

  /**
   * Initialize the IfsInstance
   * @param instance
   */
  onReady(instance: PagateqIfsInstance): void {
    this.ifsInstance.set(instance);
  }

  /**
   * callback on error while loading the payment form
   * @param err
   */
  onIfsError(err: unknown): void {
    this.error.set(this.toMessage(err) ?? this.i18n.i18nLng()['IFSFORMLOADINGFAILED']);
  }

  /**
   * Step 2 = process the payment
   */
  async pay(): Promise<void> {
    const instance = this.ifsInstance();
    const sessionId = this.sessionId();
    const transactionId = this.reference();
    if (!instance || !sessionId) return;
    const req = this.currentRequest();
    const projectId = this.projectId();
    const mode = this.paymentTypeStatus();
    this.error.set(null);
    this.submitting.set(true);
    try {
      // ✅ createToken statt generateToken
      const tokenResponse = await instance.createToken();
      const token = tokenResponse.token;
      const cc = tokenResponse.info?.creditCard ?? {};
      const brand = cc['brand'];
      // truncatedPan ist z.B. "************4242" → letzte 4 Stellen extrahieren
      const last4 = cc['truncatedPan']?.toString().slice(-4);
      /**
       * dto for girocheckout api
       */
      const motoPayment: MotoPaymentRequest = {
        sessionId: sessionId,
        projectId: projectId,
        token: token,
        transactionId: transactionId,
      }
      const result: MotoPaymentResult = await firstValueFrom(
        this.http.post<MotoPaymentResult>(this.runtimeConfig.transactionHandlingBackend + this.runtimeConfig.ifsPayEndpoint, motoPayment)
      );
      /**
       * Check the payment result from the Girocheckout api
       */
      const enriched: any = { ...result, projectId, paymentTypeStatus: mode };
      if (result.successful) {
        this.step.set('done');
        this.completed.emit(enriched);
      } else {
        this.error.set(result?.message ?? this.i18n.i18nLng()['IFSPAYMENTDECLINED']);
        this.completed.emit(enriched);
      }
    } catch (err) {
      const msg = this.toMessage(err) ?? 'Unerwarteter Fehler bei der Zahlung';
      this.error.set(msg);
      this.completed.emit({ status: 'ERROR', message: msg, projectId, paymentTypeStatus: mode });
    } finally {
      this.submitting.set(false);
    }
  }

  /**
   * Close the dialog - check for busy
   * @param force
   */
  close(force = false): void {
    if (!force && (this.busy())) return;
    this.visible.set(false);
  }

  /**
   * Fire specific events when dialog hides
   */
  onHide(): void {
    if (this.step() !== 'done') {
      this.cancelled.emit();
    } else {
      this.closed.emit();
    }
    this.resetState();
  }

  /**
   * Reset all significant data for the payment !!!
   * @private
   */
  private resetState(): void {
    this.step.set('form');
    this.sessionId.set(null);
    this.ifsInstance.set(null);
    this.ifsFormValid.set(false);
    this.error.set(null);
    this.initializing.set(false);
    this.submitting.set(false);
    this.applyInitialRequest();
    this.orderIdField.control.markAsPristine();
    this.orderIdField.control.markAsUntouched();
    this.amountField.control.markAsPristine();
    this.amountField.control.markAsUntouched();
  }

  /**
   * Reset the ninput signals form the initialization data
   * @private
   */
  applyInitialRequest(): void {
    this.orderIdInput.set('');
    this.amountInput.set(null);
    this.currencyInput.set('EUR');
  }

  /**
   * Request dto for the initialization
   * @private
   */
  private currentRequest(): MotoPaymentInitRequest {
    return {
      tenantId: this.tenantId(),
      projectId: this.projectId(),
      purpose: this.orderIdInput().trim(),
      amount: this.amountInput() ? this.amountInput() * 100 : 0,
      currency: this.currencyInput(),
    };
  }

  /**
   * Transform error object to message
   * @param err
   * @private
   */
  private toMessage(err: unknown): string | null {
    if (!err) return null;
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    return null;
  }

  /**
   * check if credit card data are valid, completed and accepted by the ifs
   * @param formValidation
   * @protected
   */
  protected onFormValidationChanged(formValidation: any) {
    this.ifsFormValid.set(formValidation.valid && formValidation.completed && formValidation.accepted);
  }

  /**
   * Credit card brand has changed - use if brand should be displayed
   * for future use
   * @param brand
   * @protected
   */
  protected onBrandChanged(brand: any) {
  }

}
